/**
 * Vue Composable for Performance Monitoring
 * 
 * Provides reactive performance tracking utilities for Vue components
 */

import { onMounted, onUnmounted, ref } from 'vue'
import { trackComponentRender, trackMetric, mark, measure } from '@/utils/performance'

/**
 * Track component lifecycle performance
 * @param {string} componentName - Name of the component
 * @returns {Object} Performance tracking utilities
 */
export function useComponentPerformance(componentName) {
  const renderTime = ref(0)
  const mountTime = ref(0)
  let renderTracker = null
  let mountStartTime = 0
  
  // Track component render
  onMounted(() => {
    mountStartTime = performance.now()
    
    // Track initial render
    if (renderTracker) {
      renderTime.value = renderTracker.end()
    }
    
    // Track mount time on next tick (after DOM updates)
    setTimeout(() => {
      mountTime.value = performance.now() - mountStartTime
      trackMetric(`Component Mount: ${componentName}`, mountTime.value)
    }, 0)
  })
  
  return {
    renderTime,
    mountTime,
    startRender: () => {
      renderTracker = trackComponentRender(componentName)
    }
  }
}

/**
 * Track async operation performance
 * @param {string} operationName - Name of the operation
 * @returns {Object} Tracking utilities
 */
export function useAsyncPerformance(operationName) {
  const duration = ref(0)
  const isTracking = ref(false)
  let startTime = 0
  
  const start = () => {
    startTime = performance.now()
    isTracking.value = true
    mark(`${operationName}-start`)
  }
  
  const end = () => {
    if (!isTracking.value) return
    
    mark(`${operationName}-end`)
    duration.value = measure(operationName, `${operationName}-start`, `${operationName}-end`)
    isTracking.value = false
    trackMetric(operationName, duration.value)
    
    return duration.value
  }
  
  return {
    duration,
    isTracking,
    start,
    end
  }
}

/**
 * Monitor render performance with automatic tracking
 * @param {Function} callback - Callback to execute
 * @param {string} label - Label for the operation
 * @returns {Function} Wrapped callback with performance tracking
 */
export function usePerformanceMonitor(callback, label) {
  return async (...args) => {
    const startTime = performance.now()
    mark(`${label}-start`)
    
    try {
      const result = await callback(...args)
      
      mark(`${label}-end`)
      const duration = measure(label, `${label}-start`, `${label}-end`)
      trackMetric(label, duration)
      
      return result
    } catch (error) {
      mark(`${label}-error`)
      throw error
    }
  }
}

/**
 * Track visualization render performance
 * @returns {Object} Visualization performance utilities
 */
export function useVisualizationPerformance() {
  const renderCount = ref(0)
  const averageRenderTime = ref(0)
  const lastRenderTime = ref(0)
  const renderTimes = []
  
  const trackRender = (duration) => {
    renderCount.value++
    lastRenderTime.value = duration
    renderTimes.push(duration)
    
    // Keep only last 10 renders
    if (renderTimes.length > 10) {
      renderTimes.shift()
    }
    
    // Calculate average
    averageRenderTime.value = renderTimes.reduce((a, b) => a + b, 0) / renderTimes.length
    
    // Log warning if render is slow
    if (duration > 500) {
      console.warn(`⚠️ Slow visualization render: ${duration.toFixed(2)}ms`)
    }
  }
  
  const startRender = () => {
    return performance.now()
  }
  
  const endRender = (startTime) => {
    const duration = performance.now() - startTime
    trackRender(duration)
    return duration
  }
  
  const reset = () => {
    renderCount.value = 0
    averageRenderTime.value = 0
    lastRenderTime.value = 0
    renderTimes.length = 0
  }
  
  return {
    renderCount,
    averageRenderTime,
    lastRenderTime,
    startRender,
    endRender,
    reset
  }
}

/**
 * Monitor memory usage (if available)
 * @returns {Object} Memory monitoring utilities
 */
export function useMemoryMonitoring() {
  const memoryUsage = ref(null)
  const isSupported = ref(false)
  
  onMounted(() => {
    // Check if memory API is available
    isSupported.value = 'memory' in performance
    
    if (isSupported.value) {
      updateMemoryUsage()
    }
  })
  
  const updateMemoryUsage = () => {
    if (!isSupported.value) return
    
    const memory = performance.memory
    memoryUsage.value = {
      usedJSHeapSize: (memory.usedJSHeapSize / 1048576).toFixed(2), // MB
      totalJSHeapSize: (memory.totalJSHeapSize / 1048576).toFixed(2), // MB
      jsHeapSizeLimit: (memory.jsHeapSizeLimit / 1048576).toFixed(2), // MB
      percentage: ((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100).toFixed(2)
    }
  }
  
  return {
    memoryUsage,
    isSupported,
    updateMemoryUsage
  }
}

export default {
  useComponentPerformance,
  useAsyncPerformance,
  usePerformanceMonitor,
  useVisualizationPerformance,
  useMemoryMonitoring
}
